import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contactInfo } from '@/mocks/homeData';

export default function ContactSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative w-full py-16 md:py-24 bg-background-100"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block text-accent-600 text-sm font-medium tracking-wider uppercase mb-2">
            Contact
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('contact.title')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div
          className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col gap-4 md:gap-5">
            <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-primary-600 text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('contact.office')}</p>
                <a
                  href={`tel:${contactInfo.officePhone}`}
                  className="text-base md:text-lg font-bold text-foreground-950 hover:text-primary-500 transition-colors"
                >
                  {contactInfo.officePhone}
                </a>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                <i className="ri-smartphone-line text-accent-600 text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('contact.mobile')}</p>
                <a
                  href={`tel:${contactInfo.mobilePhone}`}
                  className="text-base md:text-lg font-bold text-foreground-950 hover:text-primary-500 transition-colors"
                >
                  {contactInfo.mobilePhone}
                </a>
              </div>
            </div>

            <div className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 flex items-start gap-4">
              <span className="w-11 h-11 flex items-center justify-center bg-secondary-100 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-2-line text-secondary-600 text-lg"></i>
              </span>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{t('contact.address')}</p>
                <p className="text-sm md:text-base text-foreground-950">
                  {contactInfo.address}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-5">
              <a
                href={contactInfo.blog}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 flex items-start gap-4 hover:border-primary-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                  <i className="ri-youtube-fill text-lg" style={{ color: '#FF0000' }}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">{t('contact.blog')}</p>
                  <p className="text-sm md:text-base font-medium text-foreground-950">
                    3분 변호사
                  </p>
                </div>
              </a>

              <a
                href={contactInfo.naverBooking}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 flex items-start gap-4 hover:border-accent-300 transition-colors"
              >
                <span className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg flex-shrink-0">
                  <span className="font-bold text-xl leading-none" style={{ color: '#03C75A' }}>N</span>
                </span>
                <div>
                  <p className="text-xs text-foreground-500 mb-0.5">{t('contact.booking')}</p>
                  <p className="text-sm md:text-base font-medium text-foreground-950">
                    예약하기
                  </p>
                </div>
              </a>
            </div>
          </div>

          <div className="relative rounded-xl overflow-hidden border border-background-200/70 shadow-sm">
            <iframe
              src="https://www.google.com/maps?q=36.3533466,127.3882110&z=17&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: '360px' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="신가영 법률사무소 위치"
            />
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-background-50/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm pointer-events-none">
              <span className="w-6 h-6 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0">
                <i className="ri-map-pin-fill text-white text-sm"></i>
              </span>
              <div>
                <p className="text-xs font-bold text-foreground-950 whitespace-nowrap">
                  신가영 법률사무소
                </p>
                <p className="text-[11px] text-foreground-600 whitespace-nowrap">
                  민석타워 606호
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}